import { ThemeProperties as ThemePropertiesV2 } from '../version2';

export type ThemeProperties = ThemePropertiesV2;

export type ThemeContract = Required<Omit<ThemeProperties, 'version'>>;

const uSpace = (base: string, percent: number) => `${(Number.parseInt(base, 10) * percent) / 100}px`;

export const spacingBase = '8';

export function getSpacing(base: string): ThemeContract['spacing'] {
  return {
    '000': '0px',
    '025': uSpace(base, 25),
    '050': uSpace(base, 50),
    '075': uSpace(base, 75),
    '100': uSpace(base, 100),
    '150': uSpace(base, 150),
    '200': uSpace(base, 200),
    '250': uSpace(base, 250),
    '300': uSpace(base, 300),
    '400': uSpace(base, 400),
    '500': uSpace(base, 500),
    '600': uSpace(base, 600),
    '700': uSpace(base, 700),
    '800': uSpace(base, 800),
    '900': uSpace(base, 900),
    '1000': uSpace(base, 1000),
    '1200': uSpace(base, 1200),
    '1600': uSpace(base, 1600),
  };
}

export const spacing = getSpacing(spacingBase);

export const color: ThemeContract['color'] = {
  brandDark: '#4E4C4C',
  brandMedium: '#F7F6F7',
  brandLight: '#FCFCFB',
  default: '#FFFFFF',
  textDark: '#1E0036',
  textLight: '#FFFFFF',
  none: 'transparent',
  unset: 'unset',
};

const fontFamily = 'Inter';

export const fontWeight: ThemeContract['fontWeight'] = {
  light: '300',
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  variable: '100 900',
};

const typography = {
  body: {
    fontFamily,
    fontSize: '1.125rem',
    fontWeight: fontWeight.regular,
    letterSpacing: '0',
    lineHeight: '1.4',
    textDecoration: 'none',
  },
  h1: {
    fontFamily,
    fontSize: '4.5rem',
    fontWeight: fontWeight.bold,
    letterSpacing: '0',
    lineHeight: '1.2',
    textDecoration: 'none',
  },
  h2: {
    fontFamily,
    fontSize: '3rem',
    fontWeight: fontWeight.bold,
    letterSpacing: '0',
    lineHeight: '1.2',
    textDecoration: 'none',
  },
  h3: {
    fontFamily,
    fontSize: '1.5rem',
    fontWeight: fontWeight.semibold,
    letterSpacing: '0',
    lineHeight: '1.2',
    textDecoration: 'none',
  },
  h4: {
    fontFamily,
    fontSize: '1rem',
    fontWeight: fontWeight.semibold,
    letterSpacing: '0',
    lineHeight: '1.2',
    textDecoration: 'none',
  },
  cta: {
    fontFamily,
    fontSize: '1rem',
    fontWeight: fontWeight.bold,
    letterSpacing: '0',
    lineHeight: '1.2',
    textDecoration: 'none',
  },
  link: {
    fontFamily,
    fontSize: '1.125rem',
    fontWeight: fontWeight.regular,
    letterSpacing: '0',
    lineHeight: '1.2',
    textDecoration: 'underline',
  },
};

const cta: ThemeContract['cta'] = {
  primary: {
    backgroundColor: color.brandDark,
    borderColor: 'none',
    borderRadius: '12px',
    borderStyle: 'none',
    borderWidth: '0',
    color: color.textLight,
    fontWeight: fontWeight.bold,
    letterSpacing: '0',
    paddingBottom: '12px',
    paddingLeft: '20px',
    paddingRight: '20px',
    paddingTop: '12px',
    transition: 'all 0.3s ease',
    hoverBackgroundColor: '#404040',
    hoverColor: color.textLight,
  },
  secondary: {
    backgroundColor: color.brandMedium,
    borderColor: 'none',
    borderRadius: '12px',
    borderStyle: 'none',
    borderWidth: '0',
    color: color.textDark,
    fontWeight: fontWeight.bold,
    letterSpacing: '0',
    paddingBottom: '12px',
    paddingLeft: '20px',
    paddingRight: '20px',
    paddingTop: '12px',
    transition: 'all 0.3s ease',
    hoverBackgroundColor: color.brandLight,
    hoverColor: color.textDark,
  },
  tertiary: {
    backgroundColor: color.brandLight,
    borderColor: 'none',
    borderRadius: '12px',
    borderStyle: 'none',
    borderWidth: '0',
    color: color.textDark,
    fontWeight: fontWeight.bold,
    letterSpacing: '0',
    paddingBottom: '12px',
    paddingLeft: '20px',
    paddingRight: '20px',
    paddingTop: '12px',
    transition: 'all 0.3s ease',
    hoverBackgroundColor: color.brandMedium,
    hoverColor: color.textDark,
  },
};

export const image: ThemeContract['image'] = {
  borderColor: 'none',
  borderRadius: '0',
  borderStyle: 'none',
  borderWidth: '0',
  boxShadow: 'none',
};

const card: ThemeContract['card'] = {
  alignItems: 'center',
  backgroundColor: color.brandLight,
  borderColor: 'none',
  borderRadius: '0',
  borderStyle: 'none',
  borderWidth: '0',
  boxShadow: 'none',
};

export const defaultThemeV2: ThemeProperties = {
  card,
  color,
  cta,
  fontWeight,
  image,
  spacing,
  spacingBase,
  typography,
  version: 2,
};
